import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AppImage from '../../components/AppImage';
import { tournamentService } from '../../services/tournamentService';
import { regionalTeamService } from '../../services/regionalTeamService';
import { useAuth } from '../../contexts/AuthContext';
import { isRegionAdmin } from '../../utils/tanzaniaRegions';

const POSITION_LABELS = {
  point_guard: 'PG',
  shooting_guard: 'SG',
  small_forward: 'SF',
  power_forward: 'PF',
  center: 'C',
};

const RegionalTeamPlayerStatistics = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedPlayerId = searchParams.get('player');
  const { userProfile } = useAuth();
  const regionAdmin = isRegionAdmin(userProfile?.role);
  const assignedRegion = userProfile?.region || '';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Tournament Management' },
    { label: 'Regional Team Players Statistics' },
  ];

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let data = (await tournamentService.getRegionalPlayerStatistics()) || [];

      if (regionAdmin && assignedRegion) {
        if (data.some((row) => row.region)) {
          data = data.filter((row) => row.region === assignedRegion);
        } else {
          const teams = (await regionalTeamService.getAll()) || [];
          const regionTeamIds = new Set(
            teams
              .filter((team) => team.region === assignedRegion)
              .map((team) => team.id)
          );
          data = data.filter((row) => regionTeamIds.has(row.teamId));
        }
      }

      setRows(data);
    } catch (err) {
      setError(err.message || 'Failed to load regional player statistics.');
    } finally {
      setIsLoading(false);
    }
  }, [regionAdmin, assignedRegion]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (!highlightedPlayerId || isLoading || rows.length === 0) return undefined;
    const el = document.getElementById(`player-row-${highlightedPlayerId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return undefined;
  }, [highlightedPlayerId, isLoading, rows]);

  const startEdit = (row) => {
    if (regionAdmin) return;
    setEditingId(row.playerId);
    setEditForm({
      gamesPlayed: row.gamesPlayed,
      points: row.points,
      rebounds: row.rebounds,
      assists: row.assists,
      steals: row.steals,
      blocks: row.blocks,
    });
  };

  const handleSave = async (playerId) => {
    if (regionAdmin) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await tournamentService.upsertPlayerStatistics(playerId, editForm);
      setSuccess('Statistics updated.');
      setEditingId(null);
      await loadStats();
    } catch (err) {
      setError(err.message || 'Failed to save statistics.');
    } finally {
      setIsSaving(false);
    }
  };

  const highlightedPlayer = rows.find((row) => row.playerId === highlightedPlayerId);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6">
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between mt-4 mb-6 gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="BarChart3" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Regional Team Players Statistics</h1>
                <p className="text-muted-foreground">
                  {highlightedPlayer
                    ? `Viewing stats for ${highlightedPlayer.fullName}`
                    : regionAdmin
                      ? 'View-only stats for your region'
                      : 'Track stats for regional tournament players'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/public-stats')}
              >
                Back to Public Stats
              </Button>
              <Button
                variant="outline"
                iconName="LayoutDashboard"
                iconPosition="left"
                onClick={() => navigate('/admin-dashboard')}
              >
                Dashboard
              </Button>
            </div>
          </div>

          {highlightedPlayer && (
            <div className="mb-4 bg-card border border-primary/30 rounded-lg p-4 card-shadow flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-muted border border-border">
                {highlightedPlayer.photoUrl ? (
                  <AppImage
                    src={highlightedPlayer.photoUrl}
                    alt={highlightedPlayer.fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="User" size={22} className="text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-semibold text-foreground">{highlightedPlayer.fullName}</p>
                <p className="text-sm text-muted-foreground">{highlightedPlayer.teamName}</p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
                {[
                  { label: 'GP', value: highlightedPlayer.gamesPlayed },
                  { label: 'PTS', value: highlightedPlayer.points },
                  { label: 'REB', value: highlightedPlayer.rebounds },
                  { label: 'AST', value: highlightedPlayer.assists },
                  { label: 'STL', value: highlightedPlayer.steals },
                  { label: 'BLK', value: highlightedPlayer.blocks },
                ].map((stat) => (
                  <div key={stat.label} className="px-2">
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-base font-bold text-foreground">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-success/10 border-success/20 text-success">
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">
              {error}
            </div>
          )}

          <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
            {isLoading ? (
              <div className="p-8 flex items-center justify-center gap-3 text-muted-foreground">
                <Icon name="Loader2" size={20} className="animate-spin" />
                Loading statistics...
              </div>
            ) : rows.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground">
                No regional team players found. Register regional players first.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">Player</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">Team</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">Pos</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">GP</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">PTS</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">REB</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">AST</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">STL</th>
                      <th className="text-left px-4 py-2 font-medium text-muted-foreground">BLK</th>
                      {!regionAdmin && (
                        <th className="text-right px-4 py-2 font-medium text-muted-foreground">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isEditing = !regionAdmin && editingId === row.playerId;
                      const isHighlighted = highlightedPlayerId === row.playerId;
                      return (
                        <tr
                          key={row.playerId}
                          id={`player-row-${row.playerId}`}
                          className={`border-t border-border ${
                            isHighlighted ? 'bg-primary/5 ring-1 ring-inset ring-primary/30' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full overflow-hidden bg-muted">
                                {row.photoUrl ? (
                                  <AppImage
                                    src={row.photoUrl}
                                    alt={row.fullName}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Icon name="User" size={14} className="text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <span className="font-medium text-foreground">{row.fullName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-foreground">{row.teamName}</td>
                          <td className="px-4 py-3 text-foreground">
                            {POSITION_LABELS[row.position] || row.position || '—'}
                          </td>
                          {['gamesPlayed', 'points', 'rebounds', 'assists', 'steals', 'blocks'].map(
                            (field) => (
                              <td key={field} className="px-4 py-3 text-foreground">
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    value={editForm[field]}
                                    onChange={(e) =>
                                      setEditForm((prev) => ({
                                        ...prev,
                                        [field]: e.target.value,
                                      }))
                                    }
                                    className="w-16 h-8"
                                  />
                                ) : (
                                  row[field]
                                )}
                              </td>
                            )
                          )}
                          {!regionAdmin && (
                            <td className="px-4 py-3 text-right">
                              {isEditing ? (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    loading={isSaving}
                                    onClick={() => handleSave(row.playerId)}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingId(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  iconName="Pencil"
                                  onClick={() => startEdit(row)}
                                >
                                  Edit
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegionalTeamPlayerStatistics;
